import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/clients`;

/**
 * Client Service
 * Gerencia todas as operações de clientes
 */
class ClientService {
  /**
   * Get all clients
   * @returns {Promise<Array>}
   */
  async getAllClients() {
    try {
      const response = await api.get(API_BASE);
      return unwrapList(response);
    } catch (error) {
      if (error.response?.status === 404) {
        return [];
      }
      console.error("❌ Erro ao buscar clientes:", error);
      throw error;
    }
  }

  /**
   * Get client by ID
   * @param {number} id - Client ID
   * @returns {Promise<object>}
   */
  async getClientById(id) {
    try {
      const response = await api.get(
        `${API_BASE}/${id}`
      );
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar cliente ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get client addresses by contact
   * @param {string} contact - Client contact/phone
   * @returns {Promise<Array>}
   */
  async getClientAddressesByContact(contact) {
    try {
      const response = await api.get(
        `${API_BASE}/addresses/contact/${contact}`
      );
      return unwrapList(response);
    } catch (error) {
      console.error(
        `❌ Erro ao buscar endereços do contato ${contact}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Create a new client
   * @param {object} clientData - Client data
   * @returns {Promise<object>}
   */
  async createClient(clientData) {
    try {
      this.validateClientData(clientData);

      const response = await api.post(
        API_BASE,
        clientData
      );
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar cliente:", error);
      throw error;
    }
  }

  /**
   * Update client
   * @param {number} id - Client ID
   * @param {object} clientData - Updated client data
   * @returns {Promise<object>}
   */
  async updateClient(id, clientData) {
    try {
      this.validateClientData(clientData, true);

      const response = await api.put(
        `${API_BASE}/${id}`,
        clientData
      );
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar cliente ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete client by CPF
   * @param {string} cpf - Client CPF
   * @returns {Promise<void>}
   */
  async deleteClient(cpf) {
    try {
      await api.delete(`${API_BASE}/${cpf}`);
    } catch (error) {
      console.error(`❌ Erro ao deletar cliente ${cpf}:`, error);
      throw error;
    }
  }

  /**
   * Validate client data
   * @param {object} data - Client data to validate
   * @param {boolean} isUpdate - If true, some fields may be optional
   * @throws {Error} If validation fails
   */
  validateClientData(data, isUpdate = false) {
    const errors = [];

    if (!data.name || data.name.trim() === "") {
      errors.push("Nome é obrigatório");
    }

    // CPF é opcional (clientes do site costumam vir sem), mas se vier é validado
    if (data.cpf && !this.validateCPF(data.cpf)) {
      errors.push("CPF inválido");
    }

    if (data.email && !this.validateEmail(data.email)) {
      errors.push("Email inválido");
    }

    if (!data.contact || data.contact.trim() === "") {
      errors.push("Contato/Telefone é obrigatório");
    } else if (!this.validatePhone(data.contact)) {
      errors.push("Telefone inválido");
    }

    if (errors.length > 0) {
      throw new Error(errors.join(", "));
    }
  }

  /**
   * Validate CPF
   * @param {string} cpf - CPF to validate
   * @returns {boolean}
   */
  validateCPF(cpf) {
    cpf = cpf.replace(/[^\d]/g, "");

    if (cpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpf)) return false;

    let sum = 0;
    let remainder;

    for (let i = 1; i <= 9; i++) {
      sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    }

    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.substring(9, 10))) return false;

    sum = 0;
    for (let i = 1; i <= 10; i++) {
      sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    }

    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.substring(10, 11))) return false;

    return true;
  }

  /**
   * Validate email
   * @param {string} email - Email to validate
   * @returns {boolean}
   */
  validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  /**
   * Validate phone
   * @param {string} phone - Phone to validate
   * @returns {boolean}
   */
  validatePhone(phone) {
    const cleaned = phone.replace(/[^\d]/g, "");
    return cleaned.length >= 10 && cleaned.length <= 11;
  }

  /**
   * Format CPF
   * @param {string} cpf - CPF to format
   * @returns {string}
   */
  formatCPF(cpf) {
    if (!cpf) return "";
    const cleaned = cpf.replace(/[^\d]/g, "");
    if (cleaned.length !== 11) return cpf;
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }

  /**
   * Format phone
   * @param {string} phone - Phone to format
   * @returns {string}
   */
  formatPhone(phone) {
    if (!phone) return "";
    const cleaned = phone.replace(/[^\d]/g, "");
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
    }
    return phone;
  }

  /**
   * Format CEP
   * @param {string} cep - CEP to format
   * @returns {string}
   */
  formatCEP(cep) {
    if (!cep) return "";
    const cleaned = cep.replace(/[^\d]/g, "");
    if (cleaned.length !== 8) return cep;
    return cleaned.replace(/(\d{5})(\d{3})/, "$1-$2");
  }

  /**
   * Format date to Brazilian format
   * @param {string} date - ISO date string
   * @returns {string}
   */
  formatDate(date) {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("pt-BR");
  }

  /**
   * Format date and time to Brazilian format
   * @param {string} date - ISO date string
   * @returns {string}
   */
  formatDateTime(date) {
    if (!date) return "-";
    return new Date(date).toLocaleString("pt-BR");
  }
}

export default new ClientService();
